import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useUserDeets_query$data, type useUserDeets_query$key } from '@/__generated__/useUserDeets_query.graphql'
import { type useUserDeetsMutation } from '@/__generated__/useUserDeetsMutation.graphql'

type CommitAsyncFn = (data: UserDeet[]) => Promise<useUserDeets_query$data>

export type UserDeet = {
  deetId: string
  other: string | null
}

export const useUserDeets = (
  queryRef?: useUserDeets_query$key | null | undefined
): [UserDeet[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useUserDeets_query on Query {
        currentUser {
          userDeets(orderBy: [DEET_BY_DEET_ID__SEQUENCE_ASC]) {
            nodes {
              deetId
              other
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () =>
      map(
        queryData?.currentUser?.userDeets.nodes,
        (userDeet) =>
          ({
            deetId: userDeet!.deetId,
            other: userDeet!.other
          }) as UserDeet
      ),
    [queryData?.currentUser?.userDeets.nodes]
  )
  const [commit, isPending] = useMutation<useUserDeetsMutation>(graphql`
    mutation useUserDeetsMutation($input: UpdateUserDeetsInput!) {
      updateUserDeets(input: $input) {
        query {
          ...useUserDeets_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (userDeets: UserDeet[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              userDeets
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
